import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Plus, X, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { errMsg } from "@/lib/api";
import { DatePickerBR } from "./DatePickerBR";

const sel = "h-10 rounded-md border border-zap-line bg-white px-2 text-sm";
const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const nextSlot = () => {
  const d = new Date(Date.now() + 15 * 60000);
  const date = d.toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
  const [h, m] = d.toLocaleTimeString("en-GB", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" }).split(":").map(Number);
  const mins = Math.ceil((h * 60 + m) / 15) * 15;
  if (mins >= 1440) return { date: new Date(Date.now() + 86400000).toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" }), time: "00:00" };
  return { date, time: TIMES[mins / 15] };
};
export const fmtBR = (iso) => new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
export const SCHEDULE_STATUS = { agendado: ["Agendado", "bg-zap-mist text-zap-deep"], enviando: ["Enviando…", "bg-amber-100 text-amber-800"], enviado: ["Enviado", "bg-emerald-100 text-emerald-800"], erro: ["Não enviado", "bg-red-100 text-red-800"] };

export const SchedulePicker = ({ group, content, ready, onDone }) => {
  const { api } = useOutletContext();
  const [rows, setRows] = useState(() => [nextSlot()]);
  const [weekly, setWeekly] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const filled = rows.filter((r) => r.date && r.time);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/groups/${group.id}/schedule`, { ...content, times: filled.map((r) => `${r.date}T${r.time}:00`), repeat_weekly: weekly });
      toast.success(`${data.length} ${data.length === 1 ? "envio agendado" : "envios agendados"} para “${group.name}”.`);
      setRows([nextSlot()]); setWeekly(false); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <section data-testid="schedule-picker" className="space-y-3 rounded-md border border-zap-line bg-zap-mist/40 p-4">
      <h3 className="flex items-center gap-2 font-display text-base font-semibold"><CalendarClock className="h-5 w-5 text-zap-deep" />Agendar para depois</h3>
      <p className="text-sm text-muted-foreground">A mesma mensagem acima sai sozinha em cada dia e hora escolhidos (horário de Brasília). Adicione quantos horários precisar.</p>
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`schedule-row-${i}`}>
          <DatePickerBR testId={`schedule-date-${i}`} min={today()} value={r.date} onChange={(v) => set(i, "date", v)} />
          <select data-testid={`schedule-time-${i}`} value={r.time} onChange={(e) => set(i, "time", e.target.value)} className={`${sel} w-28`}>
            {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {rows.length > 1 && <Button type="button" variant="ghost" size="sm" data-testid={`schedule-remove-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remover horário"><X className="h-4 w-4" /></Button>}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" data-testid="schedule-add-row" onClick={() => setRows([...rows, { date: rows[rows.length - 1]?.date || today(), time: rows[rows.length - 1]?.time || "18:00" }])} className="gap-1" disabled={rows.length >= 50}>
        <Plus className="h-4 w-4" />Adicionar outro dia e hora
      </Button>
      <label className="flex items-center gap-3 text-sm">
        <Switch data-testid="schedule-weekly" checked={weekly} onCheckedChange={setWeekly} />
        <span className="flex items-center gap-1.5"><Repeat className="h-4 w-4" />Repetir toda semana neste dia e hora</span>
      </label>
      <Button data-testid="schedule-submit" disabled={busy || !ready || filled.length === 0} onClick={go} className="h-11 w-full gap-2">
        <CalendarClock className="h-4 w-4" />{busy ? "Agendando…" : `Agendar ${filled.length} ${filled.length === 1 ? "envio" : "envios"}`}
      </Button>
    </section>
  );
};

export const ScheduledList = ({ items, onCancel }) => (
  <section className="space-y-2">
    <h3 className="font-display text-base font-semibold">Envios agendados</h3>
    {items.length === 0 ? <p data-testid="scheduled-empty" className="text-sm text-muted-foreground">Nada agendado.</p> : (
      <ul data-testid="scheduled-list" className="divide-y divide-zap-line rounded-md border border-zap-line">
        {items.map((s) => {
          const [label, cls] = SCHEDULE_STATUS[s.status] || [s.status, ""];
          const what = [s.text, s.audio_title && `Áudio: ${s.audio_title}`, s.video_title && `Vídeo: ${s.video_title}`, s.youtube_url && "YouTube", s.link_url && `Link: ${s.link_url}`].filter(Boolean).join(" + ");
          return (
            <li key={s.id} data-testid={`scheduled-${s.id}`} className="flex items-start gap-3 px-3 py-2.5 text-sm">
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="flex flex-wrap items-center gap-2 font-semibold">
                  {fmtBR(s.run_at)}
                  <span data-testid={`scheduled-status-${s.id}`} className={`rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
                  {s.repeat_weekly && <span className="flex items-center gap-1 text-xs font-medium text-zap-deep"><Repeat className="h-3.5 w-3.5" />toda semana</span>}
                </p>
                <p className="line-clamp-2 text-muted-foreground">{what}</p>
                {s.last_sent_at && <p className="text-xs text-muted-foreground">Último envio: {fmtBR(s.last_sent_at)} · {s.last_recipients ?? 0} pessoas</p>}
                {s.last_error && <p className="text-xs font-medium text-red-700">{s.last_error}</p>}
              </div>
              {["agendado", "erro"].includes(s.status) && (
                <Button variant="ghost" size="sm" data-testid={`scheduled-cancel-${s.id}`} onClick={() => onCancel(s)} className="text-red-700">Cancelar</Button>
              )}
            </li>
          );
        })}
      </ul>
    )}
  </section>
);
